import React, { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Login from "./components/Login";
import AuthCallback from "./components/AuthCallback";
import api from "./api";
import Main from "./components/Main";
import Communications from "./components/Communications";
import AuthOutlet from "./components/AuthOutlet";
import useViewportHeight from "./hooks/useViewportHeight";
import FullPageSpinner from "./components/FullPageSpinner";
import EditCommunication from "./components/EditCommunication";
import AudioTest from "./components/AudioDeviceDemo";
import Logout from "./components/Logout";
import EditUseCasePage from "./components/admin/EditUseCasePage";
import User from "./models/User";
import { ManagementUser } from "./services/managementService";
import Requests from "./components/Requests";
import InterviewLoader from "./components/InterviewLoader";
import { UserProvider } from "./hooks/useUserContext";
import { AuthProvider } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { I18nProvider } from "./i18n";
import { NotificationsPage } from "./components/notifications/NotificationsPage";
import { AdminNotificationsComposerPage } from "./components/notifications/AdminNotificationsComposerPage";
import ErrorFallback from "./components/ErrorFallback";
import RedeemReward from "./components/RedeemReward";
import UserAdmin from "./components/admin/UserAdmin";
import CommunicationAdmin from "./components/admin/CommunicationAdmin";
import CommunicationDetail from "./components/admin/CommunicationDetail";
import UseCaseAdmin from "./components/admin/UseCaseAdmin";
import { hotjar } from "react-hotjar";
import ScrollToTop from "./components/ScrollToTop";
import StagingBanner from "./components/StagingBanner";
import InterviewUI, { InterviewUIState } from "./components/InterviewUI";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MainFeedback from "./components/MainFeedback";
import AudioDeviceDemo from "./components/AudioDeviceDemo";
import RestaurantDashboard from "./components/restaurant/RestaurantDashboard";
import RestaurantAudio from "./components/restaurant/RestaurantAudio";
import RestaurantCriteria from "./components/restaurant/RestaurantCriteria";
import RestaurantAdmin from "./components/restaurant/RestaurantAdmin";
import SocketStatus from "./components/restaurant/SocketStatus";
import OldLogin from "./components/OldLogin";
import RecentShiftsTable from "./components/restaurant/admin/RecentShiftsTable";
import ShiftTranscriptView from "./components/restaurant/admin/ShiftTranscriptView";
import TranscriptComparisonView from "./components/restaurant/admin/TranscriptComparisonView";
import ServerManagementView from "./components/restaurant/admin/ServerManagementView";
import ServerSummaryView from "./components/restaurant/admin/ServerSummaryView";
import ServerLeaderBoard from "./components/restaurant/admin/ServerLeaderBoard";
import ServerPerformanceAnalysis from "./components/restaurant/admin/ServerPerformanceAnalysis";
import CombinedServerPerformanceAnalysis from "./components/restaurant/admin/CombinedServerPerformanceAnalysis";
import LocationAdminView from "./components/restaurant/admin/LocationAdminView";
import LocationsAdminIndex from "./components/restaurant/admin/LocationsAdminIndex";
import CoverageReportView from "./components/restaurant/admin/CoverageReportView";
import PairingChallengeEditor from "./components/restaurant/admin/PairingChallengeEditor";
import CrewCalloutReviewQueue from "./components/restaurant/admin/CrewCalloutReviewQueue";
import TenantDashboard from "./components/tenants/TenantDashboard";
import PromptManagement from "./components/tenants/PromptManagement";
import QuizContentManagement from "./components/tenants/QuizContentManagement";
import RehearsalTestHarnessPage from "./components/tenants/RehearsalTestHarnessPage";
import QuizContentReview from "./components/tenants/QuizContentReview";
import TranslationReviewQueue from "./components/tenants/TranslationReviewQueue";
import CurriculumApprovalQueue from "./components/tenants/CurriculumApprovalQueue";
import PhraseRulesManagement from "./components/tenants/PhraseRulesManagement";
import TriviaManagement from "./components/tenants/TriviaManagement";
import Administration from "./components/restaurant/Administration";
import QuestionAnalysisPage from "./components/restaurant/admin/questionAnalysis/QuestionAnalysisPage";
import PermissionsMatrix from "./components/restaurant/PermissionsMatrix";
import PosInspector from "./components/restaurant/admin/PosInspector";
import NewHireTracker from "./components/restaurant/admin/NewHireTracker";
import InternalPage from "./components/restaurant/InternalPage";
import PitTimeline from "./components/restaurant/admin/PitTimeline";
import InteractionEvaluations from "./components/restaurant/admin/InteractionEvaluations";
import InteractionTypeReview from "./components/restaurant/admin/InteractionTypeReview";
import RecallReviewQueue from "./components/restaurant/admin/RecallReviewQueue";
import RecallMetricsPanel from "./components/restaurant/admin/RecallMetricsPanel";
import InteractionDisagreements from "./components/restaurant/admin/InteractionDisagreements";
import PilotTrends from "./components/restaurant/admin/PilotTrends";
import RecordingActivityReport from "./components/restaurant/admin/RecordingActivityReport";
import DemoSetup from "./components/tenants/DemoSetup";
import LiveServers from "./components/restaurant/LiveServers";
import Leaderboard from "./components/restaurant/Leaderboard";
import UpsellDashboard from "./components/restaurant/UpsellDashboard";
import PilotDashboardPage from "./components/restaurant/PilotDashboardPage";
import TrainingProgressPage from "./components/restaurant/TrainingProgressPage";
import CurriculumCoveragePage from "./components/restaurant/CurriculumCoveragePage";
import CurriculumPassportPage from "./components/restaurant/CurriculumPassportPage";
import CurriculumCoverageProvider from "./components/restaurant/curriculumCoverage/CurriculumCoverageProvider";
import GreeterScorecard from "./components/restaurant/GreeterScorecard";
import CashierScorecard from "./components/restaurant/CashierScorecard";
import ManagerScorecard from "./components/restaurant/ManagerScorecard";
import StoreScorecard from "./components/restaurant/StoreScorecard";
import ServerScorecard from "./components/restaurant/ServerScorecard";
import TeamChallenges from "./components/restaurant/TeamChallenges";
import ManagerShiftDetails from "./components/restaurant/ManagerShiftDetails";
import StoreShiftDetails from "./components/restaurant/StoreShiftDetails";
import UserManagement from "./components/restaurant/UserManagement";
import ScorecardLoginPage from "./components/auth/ScorecardLoginPage";
import PasswordChangePage from "./components/auth/PasswordChangePage";
import ForgotPasswordPage from "./components/auth/ForgotPasswordPage";
import ResetPasswordPage from "./components/auth/ResetPasswordPage";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminRoute from "./components/auth/AdminRoute";
import StaffRoute from "./components/auth/StaffRoute";
import CorporateAdminRoute from "./components/auth/CorporateAdminRoute";
import EvaluatorRoute from "./components/auth/EvaluatorRoute";
import RestaurantLandingRedirect from "./components/auth/RestaurantLandingRedirect";
import OpsView from "./components/ops/OpsView";
import ServerPerformanceHistory from "./components/restaurant/ServerPerformanceHistory";
import BespokeScore from "./components/restaurant/BespokeScore";
import BespokeScoreProfile from "./components/restaurant/BespokeScoreProfile";
import ServerHub from "./components/restaurant/mockups/ServerHub";
import MyEarnings from "./components/restaurant/mockups/MyEarnings";
import ArenaLeaderboard from "./components/restaurant/mockups/ArenaLeaderboard";
import RewardsStore from "./components/restaurant/mockups/RewardsStore";
import ShiftReplay from "./components/restaurant/mockups/ShiftReplay";
import MyStats from "./components/restaurant/mockups/MyStats";
import TeamPulse from "./components/restaurant/mockups/TeamPulse";
import MonthlyWrapped from "./components/restaurant/mockups/MonthlyWrapped";
import CharacterSheet from "./components/restaurant/mockups/CharacterSheet";
import DailyRings from "./components/restaurant/mockups/DailyRings";
import UpsellExchange from "./components/restaurant/mockups/UpsellExchange";
import BetOnYourself from "./components/restaurant/mockups/BetOnYourself";
import WhaleWatch from "./components/restaurant/mockups/WhaleWatch";
import TonightsPlan from "./components/restaurant/mockups/TonightsPlan";
import LiveRegister from "./components/restaurant/mockups/LiveRegister";
import PeopleWhoBelieve from "./components/restaurant/mockups/PeopleWhoBelieve";
import SmallWins from "./components/restaurant/mockups/SmallWins";
import HundredDollarNight from "./components/restaurant/mockups/HundredDollarNight";
import ServerOnboarding from "./components/restaurant/mockups/ServerOnboarding";
import ShiftLive from "./components/restaurant/mockups/ShiftLive";
import FlashBounties from "./components/restaurant/mockups/FlashBounties";
import CheatCodes from "./components/restaurant/mockups/CheatCodes";
import GenBbqAnalysisReport from "./components/restaurant/mockups/GenBbqAnalysisReport";
import JessCoachingPlan from "./components/restaurant/mockups/JessCoachingPlan";
import BespokeRoiCalculator from "./components/restaurant/mockups/BespokeRoiCalculator";
import BespokeServerAppMockups from "./components/restaurant/mockups/BespokeServerAppMockups";
import BespokeServerAppMockups2 from "./components/restaurant/mockups/BespokeServerAppMockups2";
import BespokeServerAppMockups3 from "./components/restaurant/mockups/BespokeServerAppMockups3";
import BespokeServerAppMockups4 from "./components/restaurant/mockups/BespokeServerAppMockups4";
import BespokeServerAppPrototype from "./components/restaurant/mockups/bespoke_server_app_prototype";
import BespokeDuolingoPrototype from "./components/restaurant/mockups/bespoke_duolingo_prototype";
import MockupIndex from "./components/restaurant/mockups/MockupIndex";
import Homepage from "./components/Homepage";
import usePageTitle from "./hooks/usePageTitle";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const DUMMY_USER: User = {
  id: "1",
  email: "test@test.com",
  isSuperuser: false,
  hasSeenVideo: true,
  useOwnVoice: false,
  managementUser: {
    id: "admin-1",
    email: "admin@test.com",
    first_name: "Admin",
    last_name: "User",
    location_id: undefined,
    role: "admin",
    tenant_id: undefined
  }
};

const App: React.FC = () => {
  useViewportHeight(); // This handles setting the --vh property which enables h-screen-dynamic to work (see tailwing.config.js)
  usePageTitle();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    console.log("Current path:", location.pathname);
  }, [location]);

  const setAndStoreToken = (token: string | null) => {
    if (token) {
      localStorage.setItem("token", token);
      setToken(token);
      fetchUserData();
    } else {
      localStorage.removeItem("token");
      setToken(null);
    }
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      Sentry.setUser({ email: user.email });
      if (hotjar.initialized()) {
        hotjar.identify("USER_ID", { email: user.email });
      }
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  useEffect(() => {
    if (token) {
      fetchUserData();
    } else {
      setUser(null);
    }
  }, [token]);

  const fetchUserData = async (): Promise<void> => {
    setLoading(true);
    try {
      const userData: User = await api.fetchUserData();
      setUser(userData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      // You might want to set an error state here and display it to the user
      setLoading(false);
      setUser(null);
      setAndStoreToken(null);
    }
  };

  const handleLogout = (): void => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };
  if (loading) {
    return <FullPageSpinner />;
  }

  return (
    <AuthProvider>
      <TenantProvider>
        <I18nProvider>
        <NotificationsProvider>
        <UserProvider initialUser={user || DUMMY_USER}>
        <ToastContainer />
        <StagingBanner />
        <ScrollToTop />
        <SentryRoutes>
        <Route
          path="/ui_test"
          element={
            <InterviewUI
              onClose={() => {}}
              title={""}
              state={InterviewUIState.LISTENING}
              onSkipSpeaking={() => {}}
              onStopRecording={() => {}}
              isAirPlayAvailable={false}
              showAirPlayPicker={() => {}}
              togglePauseReading={() => {}}
              onBack={() => {}}
              processingText={"processing"}
            />
          }
        />
        {/* feat-221 — StaffRoute, not ProtectedRoute. TenantDashboard calls five
            /api/management/* routes that are now Depends(require_any_manager_or_admin)
            (admin|corporate|manager), and it catches every failure into []. Under
            ProtectedRoute (auth only, no role) a principal outside that set reached a
            fully-rendered EMPTY dashboard instead of a denial. StaffRoute admits exactly
            the backend gate's set, so the refusal is now visible. AdminRoute would be
            stricter than the API and lock out managers/corporate — do not use it. */}
        <Route path="/tenant_management" element={<StaffRoute><TenantDashboard /></StaffRoute>} />
        <Route path="/tenant_management/demo-setup" element={<ProtectedRoute><DemoSetup /></ProtectedRoute>} />
        {/* feat-307 (spec D1) — deep links to one tenant / one location. SAME guard
            and SAME component as /tenant_management above: the dashboard reads the
            level from the path. The static demo-setup route above outranks
            :tenantId (react-router ranks static segments first). */}
        <Route path="/tenant_management/:tenantId" element={<StaffRoute><TenantDashboard /></StaffRoute>} />
        <Route path="/tenant_management/:tenantId/locations/:locationId" element={<StaffRoute><TenantDashboard /></StaffRoute>} />
        <Route path="/prompt-management" element={<ProtectedRoute><PromptManagement /></ProtectedRoute>} />
        <Route path="/restaurant/administration" element={<StaffRoute><Administration /></StaffRoute>} />
        {/* feat-338 — Admin + Corporate only. StaffRoute would admit Manager and AdminRoute
            would shut out Corporate; the analysis endpoints 403 the manager role. */}
        <Route path="/restaurant/administration/question-analysis" element={<CorporateAdminRoute><QuestionAnalysisPage /></CorporateAdminRoute>} />
        <Route path="/restaurant/administration/permissions" element={<AdminRoute><PermissionsMatrix /></AdminRoute>} />
        <Route path="/restaurant/administration/pos-inspector" element={<AdminRoute><PosInspector /></AdminRoute>} />
        {/* feat-362 — admin only, like every tool on /restaurant/internal. */}
        <Route path="/restaurant/administration/new-hires" element={<AdminRoute><NewHireTracker /></AdminRoute>} />
        <Route path="/restaurant/internal" element={<AdminRoute><InternalPage /></AdminRoute>} />
        <Route path="/restaurant/internal/timeline" element={<AdminRoute><PitTimeline /></AdminRoute>} />
        {/* feat-191 C1 — the ONLY three routes an evaluator can reach (D3). Their
            siblings above and below (/restaurant/internal, /internal/timeline,
            /internal/pilot-trends) deliberately stay <AdminRoute>. */}
        <Route path="/restaurant/internal/interaction-evaluations" element={<EvaluatorRoute><InteractionEvaluations /></EvaluatorRoute>} />
        <Route path="/restaurant/internal/interaction-evaluations/review/:interactionType" element={<EvaluatorRoute><InteractionTypeReview /></EvaluatorRoute>} />
        <Route path="/restaurant/internal/interaction-evaluations/disagreements" element={<EvaluatorRoute><InteractionDisagreements /></EvaluatorRoute>} />
        {/* feat-199 — recall QA: the double-check's DISCARDED candidates. EvaluatorRoute
            matches its siblings (D13: RBAC unchanged, require_evaluator_or_admin). */}
        <Route path="/restaurant/internal/interaction-evaluations/rejections/:interactionType" element={<EvaluatorRoute><RecallReviewQueue /></EvaluatorRoute>} />
        {/* feat-199 §6.0 — the metrics panel is ADMIN-ONLY and the backend gate moves
            to require_admin to match. Anchoring: Shane PRODUCES the overturn rate, so
            showing her the aggregate gives her a number to calibrate toward — feat-185's
            anti-anchoring logic applied to the statistic she generates. */}
        <Route path="/restaurant/internal/interaction-evaluations/recall-metrics" element={<AdminRoute><RecallMetricsPanel /></AdminRoute>} />
        <Route path="/restaurant/internal/pilot-trends" element={<AdminRoute><PilotTrends /></AdminRoute>} />
        {/* feat-342 — Bespoke-admin-only recording activity report (endpoint is require_admin). */}
        <Route path="/restaurant/internal/recording-activity" element={<AdminRoute><RecordingActivityReport /></AdminRoute>} />
        <Route path="/admin/locations" element={<StaffRoute><LocationsAdminIndex /></StaffRoute>} />
        <Route path="/admin/locations/:locationId" element={<StaffRoute><LocationAdminView /></StaffRoute>} />
        <Route path="/admin/locations/:locationId/coverage" element={<StaffRoute><CoverageReportView /></StaffRoute>} />
        <Route path="/admin/pairing-challenge" element={<AdminRoute><PairingChallengeEditor /></AdminRoute>} />
        {/* feat-289 — the crew call-out review queue. <AdminRoute>, DELIBERATELY, and
            this is the one line of the slice that is a security boundary rather than a
            preference. spec.md §5 names StaffRoute and §5 is WRONG: StaffRoute admits
            Admin AND Manager AND Corporate, and AC24 requires that no manager, corporate or
            customer-facing surface renders a delivered line — decision 5, which since the
            privacy line left the phone card (2026-09-16) is kept ONLY here. The backend is
            safe either way (every endpoint is Depends(require_admin), CONTRACT-ADMIN.md
            §0.1), so what StaffRoute would leak is not the data but the EXISTENCE of a
            queue about a manager's own staff, delivered as a rendered shell full of 403s.
            Do not "fix" this to match §5. */}
        <Route path="/crew-callouts" element={<AdminRoute><CrewCalloutReviewQueue /></AdminRoute>} />
        <Route path="/quiz-management" element={<StaffRoute><QuizContentManagement /></StaffRoute>} />
        <Route path="/quiz-management/rehearsal-test/:rehearsalId" element={<StaffRoute><RehearsalTestHarnessPage /></StaffRoute>} />
        <Route path="/quiz-review" element={<StaffRoute><QuizContentReview /></StaffRoute>} />
        <Route path="/quiz-translations" element={<StaffRoute><TranslationReviewQueue /></StaffRoute>} />
        {/* feat-270. A sibling route of the two review surfaces above, under the
            SAME StaffRoute: corporate gets manager access, so there is no second
            gating idea here. The role split that matters is inside — a store
            manager sees the queue and gets no approve control (AT-28) — and the
            real boundary is the API's 403 (AT-19), never this wrapper. */}
        <Route path="/quiz-approvals" element={<StaffRoute><CurriculumApprovalQueue /></StaffRoute>} />
        <Route path="/phrase-rules" element={<StaffRoute><PhraseRulesManagement /></StaffRoute>} />
        <Route path="/trivia-management" element={<StaffRoute><TriviaManagement /></StaffRoute>} />
        <Route path="/homepage" element={<Homepage />} />
        <Route path="/redeem/:qrCode" element={<RedeemReward />} />
        <Route path="/audio_device_demo" element={<AudioDeviceDemo />} />
        <Route
          path="/auth/google/callback"
          element={<AuthCallback setToken={setAndStoreToken} />}
        />
        <Route
          path="/login"
          element={user ? <Navigate to="/" replace /> : <Login />}
        />
        <Route path="/scorecard-login" element={<ScorecardLoginPage />} />
        <Route path="/change-password" element={<PasswordChangePage />} />
        {/* feat-163 — self-serve password reset. Public, like /scorecard-login:
            a locked-out user cannot authenticate, so these must not be gated. */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/oldlogin"
          element={user ? <Navigate to="/" replace /> : <OldLogin />}
        />
        <Route
          path="/logout"
          element={<Logout handleLogout={handleLogout} />}
        />
        <Route path="/error" element={<ErrorFallback />} />
        {/* feat-191 C2, redirect site 4 of 4. Was a hardcoded
            <Navigate to="/restaurant/pilot-dashboard" replace /> (feat-083 AC#1);
            RestaurantLandingRedirect keeps exactly that for staff and routes an
            evaluator to the interaction-evaluations page instead. */}
        <Route path="/restaurant" element={<RestaurantLandingRedirect />} />
        <Route path="/restaurant_audio" element={<RestaurantAudio />} />
        <Route path="/restaurant_criteria" element={<RestaurantCriteria />} />
        <Route path="/restaurant_admin" element={<RestaurantAdmin />} />
        <Route path="/restaurant/servers/summary" element={<StaffRoute><ServerSummaryView /></StaffRoute>} />
        <Route path="/restaurant/servers/trial" element={<StaffRoute><ServerLeaderBoard /></StaffRoute>} />
        <Route path="/restaurant/servers/leaderboard" element={<StaffRoute><ServerLeaderBoard /></StaffRoute>} />
        <Route path="/restaurant/servers/performance" element={<StaffRoute><ServerPerformanceAnalysis /></StaffRoute>} />
        <Route path="/restaurant/servers/combined-performance" element={<StaffRoute><CombinedServerPerformanceAnalysis /></StaffRoute>} />
        <Route path="/restaurant/server-scorecard" element={<StaffRoute><ServerScorecard /></StaffRoute>} />
        <Route path="/restaurant/server-performance-history" element={<StaffRoute><ServerPerformanceHistory /></StaffRoute>} />
        <Route path="/restaurant/team-challenges" element={<StaffRoute><TeamChallenges /></StaffRoute>} />
        <Route path="/restaurant/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/restaurant/administration/notifications" element={<StaffRoute><AdminNotificationsComposerPage /></StaffRoute>} />
        {/* <Route path="/restaurant/cashier-scorecard" element={<CashierScorecard />} /> */}
        {/* <Route path="/restaurant/cashier-performance-history" element={<CashierPerformanceHistory />} /> */}
        {/* <Route path="/restaurant/greeter-scorecard" element={<GreeterScorecard />} /> */}
        {/* <Route path="/restaurant/greeter-performance-history" element={<GreeterPerformanceHistory />} /> */}
        {/* <Route path="/restaurant/greeter-greeting-performance-history" element={<GreeterGreetingPerformanceHistory />} /> */}
        <Route
          path="/restaurant/manager-scorecard"
          element={
            <StaffRoute>
              <ManagerScorecard />
            </StaffRoute>
          }
        />
        <Route
          path="/restaurant/manager-shift-details"
          element={
            <StaffRoute>
              <ManagerShiftDetails />
            </StaffRoute>
          }
        />
        <Route
          path="/restaurant/store-scorecard"
          element={
            <StaffRoute>
              <StoreScorecard />
            </StaffRoute>
          }
        />
        <Route
          path="/restaurant/store-shift-details"
          element={
            <StaffRoute>
              <StoreShiftDetails />
            </StaffRoute>
          }
        />
        <Route
          path="/restaurant/leaderboard"
          element={
            <StaffRoute>
              <Leaderboard />
            </StaffRoute>
          }
        />
        <Route
          path="/restaurant/upsell-dashboard"
          element={
            <StaffRoute>
              <UpsellDashboard />
            </StaffRoute>
          }
        />
        {/* Top-tab placeholders — real content lands feat-053b / feat-053c */}
        <Route
          path="/restaurant/pilot-dashboard"
          element={
            <StaffRoute>
              <PilotDashboardPage />
            </StaffRoute>
          }
        />
        <Route
          path="/restaurant/training-progress"
          element={
            <StaffRoute>
              <TrainingProgressPage />
            </StaffRoute>
          }
        />
        {/* feat-241 — Curriculum Coverage. A sibling of Training Progress (P1), not a
            sub-tab inside it: TrainingProgressPage is a rolling-14-day ADHERENCE grid
            with a goal basis, and coverage is a different question. The passport is
            its own linkable URL (P5) rather than a dialog, so it is shareable and the
            back button works.

            A LAYOUT ROUTE, and that is load-bearing rather than tidiness: the provider
            owns the single fetch, so drilling from a matrix row into a passport keeps
            it MOUNTED and the passport renders from the response the matrix already
            has — no second network call and no second evaluator path (P12, AC18).
            StaffRoute wraps the parent, so it gates BOTH children (AC13); an evaluator
            reaches neither, and EVALUATOR_TABS is untouched. */}
        <Route
          path="/restaurant/curriculum-coverage"
          element={
            <StaffRoute>
              <CurriculumCoverageProvider />
            </StaffRoute>
          }
        >
          <Route index element={<CurriculumCoveragePage />} />
          <Route path=":serverId" element={<CurriculumPassportPage />} />
        </Route>
        <Route path="/restaurant/demos" element={<ProtectedRoute><MockupIndex /></ProtectedRoute>} />
        <Route path="/restaurant/mockups" element={<ProtectedRoute><ServerHub /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/my-earnings" element={<ProtectedRoute><MyEarnings /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/leaderboard" element={<ProtectedRoute><ArenaLeaderboard /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/rewards" element={<ProtectedRoute><RewardsStore /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/shift-replay" element={<ProtectedRoute><ShiftReplay /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/my-stats" element={<ProtectedRoute><MyStats /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/team-pulse" element={<ProtectedRoute><TeamPulse /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/wrapped" element={<ProtectedRoute><MonthlyWrapped /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/character" element={<ProtectedRoute><CharacterSheet /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/rings" element={<ProtectedRoute><DailyRings /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/exchange" element={<ProtectedRoute><UpsellExchange /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/bet" element={<ProtectedRoute><BetOnYourself /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/whales" element={<ProtectedRoute><WhaleWatch /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/tonights-plan" element={<ProtectedRoute><TonightsPlan /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/live-register" element={<ProtectedRoute><LiveRegister /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/people-who-get-it" element={<ProtectedRoute><PeopleWhoBelieve /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/small-wins" element={<ProtectedRoute><SmallWins /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/set-your-number" element={<ProtectedRoute><HundredDollarNight /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/onboarding" element={<ProtectedRoute><ServerOnboarding /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/shift-live" element={<ProtectedRoute><ShiftLive /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/flash-bounties" element={<ProtectedRoute><FlashBounties /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/cheat-codes" element={<ProtectedRoute><CheatCodes /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/gen-bbq-analysis" element={<ProtectedRoute><GenBbqAnalysisReport /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/jess-coaching-plan" element={<ProtectedRoute><JessCoachingPlan /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/roi-calculator" element={<ProtectedRoute><BespokeRoiCalculator /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/server-app-styles" element={<ProtectedRoute><BespokeServerAppMockups /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/server-app-styles-2" element={<ProtectedRoute><BespokeServerAppMockups2 /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/server-app-styles-3" element={<ProtectedRoute><BespokeServerAppMockups3 /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/server-app-styles-4" element={<ProtectedRoute><BespokeServerAppMockups4 /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/server-app-prototype" element={<ProtectedRoute><BespokeServerAppPrototype /></ProtectedRoute>} />
        <Route path="/restaurant/mockups/duolingo-prototype" element={<ProtectedRoute><BespokeDuolingoPrototype /></ProtectedRoute>} />
        <Route
          path="/restaurant/bespoke-score"
          element={
            <StaffRoute>
              <BespokeScore />
            </StaffRoute>
          }
        />
        <Route
          path="/restaurant/bespoke-score/:serverId"
          element={
            <StaffRoute>
              <BespokeScoreProfile />
            </StaffRoute>
          }
        />
        <Route
          path="/restaurant/live-servers"
          element={
            <StaffRoute>
              <LiveServers />
            </StaffRoute>
          }
        />
        {/* feat-221 — same mismatch as /tenant_management, found while verifying that
            one was the only one. UserManagement calls GET /api/management/locations
            (UserManagement.tsx:57) and GET /api/management/servers (:110), both now
            Depends(require_any_manager_or_admin). On 403 the locations fetch is caught
            into [] and `availableLocations` silently substitutes the hardcoded
            `fallbackStoreLocations` names, so a non-manager saw invented store names
            plus a "Failed to fetch servers" banner. StaffRoute = the backend's set. */}
        <Route
          path="/restaurant/user-management"
          element={
            <StaffRoute>
              <UserManagement />
            </StaffRoute>
          }
        />
        <Route
          path="/restaurant/shifts"
          element={
            <StaffRoute>
              <RecentShiftsTable />
            </StaffRoute>
          }
        />
        <Route
          path="/restaurant/shifts/:shiftId/transcript/:serverId"
          element={<StaffRoute><ShiftTranscriptView /></StaffRoute>}
        />
        <Route
          path="/restaurant/transcript-compare"
          element={<StaffRoute><TranscriptComparisonView /></StaffRoute>}
        />
        <Route path="/restaurant/servers" element={<StaffRoute><ServerManagementView /></StaffRoute>} />
        <Route
          path="/restaurant/servers/summary"
          element={<StaffRoute><ServerSummaryView /></StaffRoute>}
        />
        <Route
          path="/restaurant/servers/trial"
          element={<StaffRoute><ServerLeaderBoard /></StaffRoute>}
        />
        <Route
          path="/restaurant/servers/leaderboard"
          element={<StaffRoute><ServerLeaderBoard /></StaffRoute>}
        />
        <Route
          path="/restaurant/servers/performance"
          element={<StaffRoute><ServerPerformanceAnalysis /></StaffRoute>}
        />
        <Route
          path="/restaurant/servers/combined-performance"
          element={<StaffRoute><CombinedServerPerformanceAnalysis /></StaffRoute>}
        />
        {/* feat-274: GET /socket-status now requires admin on the backend, and
            the page is nothing but that endpoint. Without this guard a non-admin
            gets a permanently-loading page instead of a redirect. */}
        <Route
          path="/socket-status"
          element={
            <AdminRoute>
              <SocketStatus />
            </AdminRoute>
          }
        />
        <Route
          path="/ops"
          element={
            <AdminRoute>
              <OpsView />
            </AdminRoute>
          }
        />
        
        {/* Public root route */}
        <Route path="/" element={<Homepage />} />
        
        {/* Admin Routes */}
        <Route
          element={
            <AuthOutlet
              isAuthenticated={user != null && user.isSuperuser}
              fallbackPath="/login"
            />
          }
        >
          <Route path="/admin/use-case" element={<UseCaseAdmin />} />
          <Route path="/admin/use-case/new" element={<EditUseCasePage />} />
          <Route path="/admin/use-case/:id" element={<EditUseCasePage />} />
          <Route path="/admin/user" element={<UserAdmin />} />
          <Route path="/admin/communication" element={<CommunicationAdmin />} />
          <Route
            path="/admin/communication/:id"
            element={<CommunicationDetail />}
          />
        </Route>

        {/* Authenticated routes */}
        <Route
          element={
            <AuthOutlet isAuthenticated={user != null} fallbackPath="/login" />
          }
        >
          <Route path="/oldmain" element={<Main />} />
          <Route path="/main" element={<MainFeedback />} />
          <Route path="/interview" element={<InterviewLoader />} />
          <Route path="/communication" element={<Communications />} />
          <Route path="/request" element={<Requests />} />
          <Route
            path="/communication/:communicationId"
            element={<EditCommunication />}
          />
        </Route>
      </SentryRoutes>
        </UserProvider>
        </NotificationsProvider>
        </I18nProvider>
      </TenantProvider>
    </AuthProvider>
  );
};

export default App;
